import React from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Top-level error boundary that catches render-time exceptions anywhere in
 * the app and shows a recovery card instead of a blank screen. Click the
 * button to soft-reload — preserves auth (cookies/localStorage) but resets
 * React state.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    // `inline` mode: render a compact inline card instead of taking over the
    // whole screen. Useful for wrapping individual widgets (a broken KPI card
    // shouldn't kill the whole dashboard).
    if (this.props.inline) {
      return (
        <div
          data-testid={this.props.testid || "error-boundary-inline"}
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <div className="flex items-center gap-2 font-semibold mb-1">
            <AlertTriangle className="h-4 w-4" />
            <span>{this.props.label || "Componente indisponível"}</span>
          </div>
          {this.state.error?.message ? (
            <pre className="text-[11px] text-red-600/80 whitespace-pre-wrap break-words max-h-24 overflow-auto">
              {String(this.state.error.message)}
            </pre>
          ) : null}
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 text-xs text-red-700 underline hover:no-underline"
          >
            Tentar novamente
          </button>
        </div>
      );
    }
    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
      >
        <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl p-8 shadow-lg space-y-4">
          <div className="flex items-center gap-3 text-red-600">
            <AlertTriangle className="h-6 w-6" />
            <h1 className="text-xl font-semibold">Algo deu errado</h1>
          </div>
          <p className="text-sm text-slate-600">
            Ocorreu um erro inesperado. Sua sessão continua ativa — basta recarregar a página para
            continuar de onde parou.
          </p>
          {this.state.error?.message ? (
            <pre className="text-xs bg-slate-100 p-3 rounded text-slate-700 whitespace-pre-wrap break-words max-h-40 overflow-auto">
              {String(this.state.error.message)}
            </pre>
          ) : null}
          <Button
            data-testid="error-boundary-reload-btn"
            onClick={this.handleReset}
            className="w-full"
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            Recarregar
          </Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
